import { useEffect, useState } from 'react';
import { resolvePlacePhoto } from '../services/placePhotoApi';
import type { ActivityImage } from '../types';

/**
 * Which picture an activity row should show, and what to do when it fails.
 *
 * The ladder is user photo → venue photo → pool photo → nothing. The server
 * ships every rung it has in one payload, because it cannot know whether the
 * venue resolution we make from here will succeed: shipping only the best
 * candidate would leave a broken frame with nothing behind it whenever a
 * resolution 404s, times out, or gets throttled.
 *
 * So this hook never leaves the row without an answer. It starts on the best
 * rung it can show immediately, upgrades to the venue photo only once that URI
 * is in hand, and steps back down on any failure — including a failure of the
 * <img> itself, which is the only way to catch a URI that resolved and then
 * expired before the browser fetched it.
 *
 * Venue resolutions are deliberately serialised across the page by a small
 * in-flight cap: each one spends a metered call and holds a slot on a fleet of
 * four workers, so a day view with a dozen rows must not open a dozen at once.
 */

const MAX_IN_FLIGHT = 2;
let inFlight = 0;
const waiting: Array<() => void> = [];

interface Ticket {
  granted: Promise<void>;
  /** Idempotent: safe from the effect cleanup AND the request's finally. */
  cancel: () => void;
}

/**
 * A place in the queue, which is not the same thing as a slot.
 *
 * `cancel()` has to tell them apart. A row that unmounts while still WAITING
 * never held a slot, so releasing one on its behalf hands a slot to the next
 * waiter while both current holders are still running — the cap stops binding
 * exactly when a long list is scrolling, which is when it matters. And cancel
 * is called twice on the normal path (once by the request's `finally`, once by
 * the effect cleanup), so it has to be idempotent or every completed resolution
 * releases two slots.
 */
function acquire(): Ticket {
  let state: 'queued' | 'held' | 'done' = 'held';
  let resolveGranted: () => void = () => {};
  let grantSlot: () => void = () => {};

  let granted: Promise<void>;
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight += 1;
    granted = Promise.resolve();
  } else {
    state = 'queued';
    granted = new Promise<void>((resolve) => {
      resolveGranted = resolve;
    });
    // The queue holds a WRAPPER, not the bare resolver, so that being granted
    // flips the state in the same synchronous step that hands the slot over.
    // Converting in a `.then` instead left a window — the slot already shifted
    // out of the queue, the ticket still marked 'queued' — in which a cancel
    // found nothing to splice and returned without releasing. That slot was
    // gone for good: unlike the server's lease there is no TTL out here to
    // restore it, so two such cancels wedge the venue tier for the session.
    // A single React commit that unmounts a holder before a queued row hits it
    // exactly.
    grantSlot = () => {
      if (state === 'queued') state = 'held';
      resolveGranted();
    };
    waiting.push(grantSlot);
  }

  return {
    granted,
    cancel: () => {
      if (state === 'done') return;
      if (state === 'queued') {
        const index = waiting.indexOf(grantSlot);
        if (index >= 0) {
          waiting.splice(index, 1);   // still queued: just leave the queue
          state = 'done';
          return;
        }
        // Not in the queue and not yet converted should now be impossible, but
        // if it ever happens again the slot must come back rather than vanish.
        state = 'done';
        release();
        return;
      }
      state = 'done';
      release();
    },
  };
}

function release(): void {
  const next = waiting.shift();
  if (next) {
    next();               // hand the slot straight to the next waiter
    return;
  }
  inFlight = Math.max(0, inFlight - 1);
}

/** Test-only view of the gate; production code must not use it. */
export const __inFlightForTest = () => ({ inFlight, waiting: waiting.length });

/** Test-only. The gate is module state, so it outlives a test that leaves
 *  a resolution pending — which is exactly what the cap tests do. */
export const __resetGateForTest = () => {
  inFlight = 0;
  waiting.length = 0;
};

export interface ResolvedActivityImage {
  /** The URL to render, or '' when this row shows no picture at all. */
  url: string;
  /** Which rung produced it — the caller credits accordingly. */
  tier: 'user' | 'venue' | 'pool' | 'none';
  photographer?: string;
  /** Where the credit should link: Unsplash for pool, Google Maps for venue. */
  creditUrl?: string;
  /** Call when the rendered image itself fails to load. */
  onError: () => void;
}



export function useActivityImage(
  image: ActivityImage | null | undefined,
  width = 400,
): ResolvedActivityImage {
  // What we know about the venue rung FOR THE CURRENT place. Both are keyed to
  // the place id, because an item can be given a different one without ever
  // unmounting — AI edits update rows in place — and carrying either value
  // across that change is wrong in a way the user sees: a stale URL renders
  // another venue's photo, and a stale failure disables the rung permanently.
  const [venueState, setVenueState] = useState<{
    placeId: string;
    failed: boolean;
    photo: { url: string; credit: string; author: string } | null;
  } | null>(null);
  // Rungs whose image was served but would not RENDER. A URL can be present and
  // still broken — an expired Google URI, a pruned pool photo, a user upload
  // whose object is gone — and a ladder that only demotes on request failures
  // leaves those rows showing a broken frame with a working rung underneath.
  const [brokenUrls, setBrokenUrls] = useState<string[]>([]);
  const markBroken = (url: string) =>
    setBrokenUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));

  const placeId = image?.place_id;
  const current = venueState && venueState.placeId === placeId ? venueState : null;
  const userPhotoUrl = image?.user_photo_url && !brokenUrls.includes(image.user_photo_url)
    ? image.user_photo_url
    : undefined;
  const wantsVenue = Boolean(placeId) && !userPhotoUrl && !current?.failed;

  const poolRung = (): ResolvedActivityImage => {
    const pool = image?.pool;
    const poolUrl = pool ? pool.thumb_url || pool.url : '';
    if (pool && poolUrl && !brokenUrls.includes(poolUrl)) {
      return {
        url: poolUrl,
        tier: 'pool',
        photographer: pool.photographer,
        creditUrl: pool.unsplash_link,
        onError: () => markBroken(poolUrl),
      };
    }
    return { url: '', tier: 'none', onError: () => {} };
  };

  useEffect(() => {
    if (!wantsVenue || !placeId) return undefined;

    // Per-run, NOT a ref: a ref is shared across effect runs, so re-running for
    // a new place id would un-cancel the previous run and let its late
    // resolution overwrite the new one's.
    let cancelled = false;
    const ticket = acquire();
    // Abandoning a row has to abandon its REQUEST. The cap frees the client
    // slot the moment a row unmounts, so a request left running would keep
    // holding a server slot while the next row was already given the client
    // one — and enough scrolling would put arbitrarily many of those in flight.
    const abortController = new AbortController();

    ticket.granted.then(async () => {
      if (cancelled) {
        ticket.cancel();
        return;
      }
      try {
        const result = await resolvePlacePhoto(placeId, width, abortController.signal);
        if (!cancelled) {
          setVenueState({
            placeId,
            failed: false,
            photo: {
              url: result.photo_uri,
              credit: result.google_maps_uri,
              author: result.author_attributions?.[0]?.display_name || '',
            },
          });
        }
      } catch {
        // Any failure — 404 (no photo), 429 (busy or capped), timeout, the
        // feature being off — means the same thing to a row: use the rung
        // below. None of them is worth showing anyone.
        if (!cancelled) setVenueState({ placeId, failed: true, photo: null });
      } finally {
        ticket.cancel();
      }
    });

    return () => {
      cancelled = true;
      abortController.abort();
      ticket.cancel();
    };
  }, [placeId, width, wantsVenue]);

  if (userPhotoUrl) {
    return {
      url: userPhotoUrl,
      tier: 'user',
      // Their own photo can 404 too (a deleted object, a bad upload). Falling
      // through to the venue or pool rung beats a broken frame on the row.
      onError: () => markBroken(userPhotoUrl),
    };
  }
  // `failed` is checked on the way OUT as well as on the way in: a URI that
  // loaded into state and then failed to render is still in state, and
  // returning it again would leave the row broken forever.
  if (current?.photo?.url && !current.failed) {
    return {
      url: current.photo.url,
      tier: 'venue',
      photographer: current.photo.author,
      creditUrl: current.photo.credit,
      // A resolved URI can still expire before it is fetched. Falling back here
      // is what keeps that from being a broken image.
      onError: () => setVenueState({ placeId: placeId as string, failed: true, photo: null }),
    };
  }
  return poolRung();
}
